import { api } from './client';
import type { ApiResponse, EntityId } from './types';

/** Ports `core/services/notifications-api.service.ts`. */

/**
 * One notification.
 *
 * All three ids are UUID STRINGS, verified against a live response:
 * `notification_id: "019fae11-eeef-789f-b577-62788c7a93fa"`. The Angular interface declared them as
 * `number`, so `markAsRead(id: number)` was being handed a UUID — it happened to work because the id
 * was only ever interpolated into a URL and compared with `===`, both of which are string-safe.
 */
export interface AppNotification {
  notification_id: EntityId;
  notification_type: string;
  title: string;
  message: string;
  entity_type: string;
  entity_id: EntityId;
  workflow_instance_id: EntityId | null;
  action_url: string | null;
  is_read: boolean;
  read_at: string | null;
  created_at: string;
}

export interface NotificationsData {
  total: number;
  unread_count: number;
  unread_by_type: Record<string, number>;
  notifications: AppNotification[];
}

export interface MarkAllReadResponse {
  marked_as_read: number;
}

/**
 * ⚠️ THE TRAILING SLASH ON `/notifications/` IS LOAD-BEARING. DO NOT REMOVE IT.
 *
 * The backend serves this collection at `/api/v1/notifications/` and answers the slash-less form
 * with a redirect to the ABSOLUTE upstream URL, `http://<host>:1006/api/v1/notifications/`.
 *
 * Because our base is relative, the request starts at the dev/nginx origin, so following that
 * redirect crosses origins — and the browser drops `Authorization` on a cross-origin redirect. The
 * result is `401 {"status":false,"message":"Authentication token required"}` on a request that was
 * sent with a perfectly good token, which reads as an expired session and logs the user out.
 *
 * It worked in Angular only by accident: its base URL was the absolute upstream, so the redirect
 * was same-origin and the header survived. Requesting the real path is the fix in both worlds, and
 * it also removes a redirect round-trip.
 *
 * Verified against the live API: slash → `401 Authentication token required` (reached the app);
 * no slash → an opaque cross-origin redirect. `/notifications/read-all` and
 * `/notifications/{id}/read` do NOT redirect — they were probed alongside every other static path
 * in the API surface, and this is the only endpoint affected.
 */
export const notificationsApi = {
  getNotifications: (unreadOnly = false) =>
    api
      .get<ApiResponse<NotificationsData>>('/notifications/', {
        // Angular sent the literal string 'True' (Python-style), not 'true'.
        params: unreadOnly ? { unread: 'True' } : undefined,
        meta: { fallbackMessage: 'Failed to load notifications' },
      })
      .then((response) => response.data.data),

  markAsRead: (notificationId: EntityId) =>
    api
      .patch<AppNotification>(`/notifications/${notificationId}/read`, {}, {
        meta: { fallbackMessage: 'Failed to mark notification as read' },
      })
      .then((response) => response.data),

  markAllAsRead: () =>
    api
      .post<MarkAllReadResponse>('/notifications/read-all', {}, {
        meta: { fallbackMessage: 'Failed to mark all notifications as read' },
      })
      .then((response) => response.data),
};
